import { create } from "zustand";
import { api, setAccessToken } from "@/lib/api-client";

export interface AuthUser {
  id: string;
  fullName: string;
  email: string;
  role: "CONSUMER" | "AGENT";
}

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isInitialized: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  registerConsumer: (input: {
    fullName: string;
    email: string;
    password: string;
    phone?: string;
  }) => Promise<AuthUser>;
  registerAgent: (input: {
    fullName: string;
    email: string;
    password: string;
    phone?: string;
    inviteCode: string;
  }) => Promise<AuthUser>;
  logout: () => Promise<void>;
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: false,
  isInitialized: false,

  login: async (email, password) => {
    set({ isLoading: true });
    try {
      const data = await api.post<{
        accessToken: string;
        refreshToken: string;
        user: AuthUser;
      }>("/auth/login", { email, password });
      setAccessToken(data.accessToken);            // uses shared helper
      localStorage.setItem('refreshToken', data.refreshToken);
      set({ user: data.user, isLoading: false });
      return data.user;
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  registerConsumer: async (input) => {
    set({ isLoading: true });
    try {
      const data = await api.post<{
        accessToken: string;
        refreshToken: string;
        user: AuthUser;
      }>("/auth/register", input);
      setAccessToken(data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      set({ user: data.user, isLoading: false });
      return data.user;
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  registerAgent: async (input) => {
    set({ isLoading: true });
    try {
      const data = await api.post<{
        accessToken: string;
        refreshToken: string;
        user: AuthUser;
      }>("/auth/register-agent", input);
      setAccessToken(data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      set({ user: data.user, isLoading: false });
      return data.user;
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  logout: async () => {
    await api.post("/auth/logout").catch(() => {});
    setAccessToken(null);
    localStorage.removeItem('refreshToken');
    set({ user: null });
  },

  initialize: async () => {
    const storedAccess = localStorage.getItem('accessToken');
    if (storedAccess) {
      setAccessToken(storedAccess);
      try {
        const me = await api.get<{ user: AuthUser }>("/auth/me");
        set({ user: me.user, isInitialized: true });
        return;
      } catch {
        // token expired – try refresh
      }
    }

    const storedRefresh = localStorage.getItem('refreshToken');
    if (storedRefresh) {
      try {
        const refreshData = await api.post<{
          accessToken: string;
          refreshToken: string;
          user: AuthUser;
        }>("/auth/refresh", undefined, {
          skipAuth: true,
          headers: { Authorization: `Bearer ${storedRefresh}` },
        });
        setAccessToken(refreshData.accessToken);
        localStorage.setItem('refreshToken', refreshData.refreshToken);
        set({ user: refreshData.user, isInitialized: true });
        return;
      } catch {
        // refresh failed – clear tokens
      }
    }

    setAccessToken(null);
    localStorage.removeItem('refreshToken');
    set({ user: null, isInitialized: true });
  },
}));